import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Box,
  VStack,
  Text,
  Center,
  Button,
  HStack,
  Flex,
  IconButton,
  Spinner,
  useDisclosure,
} from '@chakra-ui/react';
import { RepeatIcon, SettingsIcon } from '@chakra-ui/icons';
import { useDispatch, useSelector } from 'react-redux';

import OrdersFilterSection from './OrdersFilterSection';
import OrdersTable from './OrdersTable';
import OrdersPagination from './OrdersPagination';
import OrdersSyncSettingsModal from './OrdersSyncSettingsModal';
import { getOrdersApi, getOrdersOverviewApi } from 'features/orders/orders.api';
import { getStoresListApi } from 'features/stores/stores.api';
import { useColors } from './colors';

const OrdersTableContainer = () => {
  const dispatch = useDispatch();
  const { mutedTextColor } = useColors();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { orders, totalOrders, totalPages, currentPage: reduxPage, getOrdersStatus, syncRunning } =
    useSelector((s) => s.orders);
  const storesList = useSelector((s) => s.stores.list || []);

  const [searchField, setSearchField] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [sortBy, setSortBy] = useState('placedAt-');
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    marketplace: '',
    store: '',
    fulfillmentStatus: '',
    dateFrom: '',
    dateTo: '',
    lineItemCount: '',
  });

  const storeOptions = useMemo(
    () =>
      storesList.map((s) => ({
        _id: s.id,
        name: s.name || s.label,
      })),
    [storesList],
  );

  const buildFetchParams = useCallback(
    (nextFilters, page = currentPage) => ({
      page,
      limit: pageSize,
      search: appliedSearch,
      searchField,
      sortBy,
      marketplace: nextFilters.marketplace,
      store: nextFilters.store,
      fulfillmentStatus: nextFilters.fulfillmentStatus,
      dateFrom: nextFilters.dateFrom,
      dateTo: nextFilters.dateTo,
      lineItemCount: nextFilters.lineItemCount,
    }),
    [currentPage, pageSize, appliedSearch, searchField, sortBy],
  );

  const fetchOrders = useCallback(
    (overrides = {}) => {
      dispatch(getOrdersApi({ ...buildFetchParams(filters), ...overrides }));
    },
    [dispatch, buildFetchParams, filters],
  );

  useEffect(() => {
    dispatch(getStoresListApi());
  }, [dispatch]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleFilterChange = (field, value) => {
    if (field === 'clear') {
      setFilters(value);
    } else {
      setFilters((prev) => ({ ...prev, [field]: value }));
    }
    setCurrentPage(1);
    setPageInput(1);
  };

  const handleSearch = () => {
    setAppliedSearch(searchQuery.trim());
    setCurrentPage(1);
    setPageInput(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setPageInput(page);
  };

  const handleSyncComplete = () => {
    dispatch(getOrdersOverviewApi());
    fetchOrders();
  };

  const loading = getOrdersStatus === 'pending';
  const effectiveTotalPages = totalPages || 1;
  const effectiveTotal = totalOrders ?? orders.length;

  return (
    <Box>
      <VStack spacing={5} align="stretch">
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
          <Text fontSize="sm" color={mutedTextColor}>
            Orders synced from eBay Fulfillment API and Amazon SP-API Orders v0.
          </Text>
          <HStack spacing={2}>
            <IconButton
              aria-label="Order sync settings"
              icon={<SettingsIcon />}
              size="sm"
              variant="outline"
              onClick={onOpen}
            />
            <Button
              size="sm"
              leftIcon={syncRunning ? <Spinner size="xs" /> : <RepeatIcon />}
              variant="outline"
              onClick={onOpen}
              isDisabled={syncRunning}
            >
              {syncRunning ? 'Syncing…' : 'Sync orders'}
            </Button>
          </HStack>
        </Flex>

        <OrdersFilterSection
          searchField={searchField}
          setSearchField={setSearchField}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          onSearch={handleSearch}
          filters={filters}
          onFilterChange={handleFilterChange}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          storeOptions={storeOptions}
        />

        {loading && orders.length === 0 ? (
          <Center py={16}>
            <Spinner size="lg" />
          </Center>
        ) : orders.length === 0 ? (
          <Center py={16}>
            <VStack spacing={2}>
              <Text fontWeight="medium">No orders found</Text>
              <Text fontSize="sm" color={mutedTextColor}>
                Run a sync from settings to import orders from your marketplaces.
              </Text>
              <Button size="sm" colorScheme="blue" onClick={onOpen}>
                Open sync settings
              </Button>
            </VStack>
          </Center>
        ) : (
          <>
            <OrdersTable
              orders={orders}
              sortBy={sortBy}
              setSortBy={(v) => {
                setSortBy(v);
                setCurrentPage(1);
                setPageInput(1);
              }}
              pageSize={pageSize}
              setPageSize={(v) => {
                setPageSize(v);
                setCurrentPage(1);
                setPageInput(1);
              }}
              currentPage={reduxPage || currentPage}
              totalOrders={effectiveTotal}
            />
            <OrdersPagination
              currentPage={reduxPage || currentPage}
              totalPages={effectiveTotalPages}
              pageInput={pageInput}
              setPageInput={setPageInput}
              onPageChange={handlePageChange}
              hasOrders={orders.length > 0}
            />
          </>
        )}
      </VStack>

      <OrdersSyncSettingsModal
        isOpen={isOpen}
        onClose={onClose}
        onSyncComplete={handleSyncComplete}
      />
    </Box>
  );
};

export default OrdersTableContainer;
